import { Component, OnInit, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { LoginService } from '../../shared/services/login.service';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Errors, FormValidationService } from '../../shared/forms';
import { NomidioLoginService } from '../services/nomidio-login.service';
import { MatDialog } from '@angular/material/dialog';
import { MessageDialogConfig, MessageDialogTheme, MessageDialogComponent } from '../../admin/dialogs/message-dialog/message-dialog.component';
import { firstValueFrom } from 'rxjs';

export enum OauthError {
  InvalidParameters = 'invalidParameters',
  AuthorisationError = 'authorisationError',
  UnexpectedFailure = 'unexpectedFailure'
}

export enum LoginError {
  LoginTimeout = 'loginTimeout',
  InvalidCredentials = 'invalidCredentials',
}

@Component({
  styleUrls: ['./login.component.scss'],
  templateUrl: './login.component.html',
  standalone: false
})
export class LoginComponent implements OnInit {
  private readonly loginService = inject(LoginService);
  private readonly nomidioLoginService = inject(NomidioLoginService);
  private readonly router = inject(Router);
  private readonly dialog = inject(MatDialog);
  readonly activatedRoute = inject(ActivatedRoute);
  readonly controls = inject(FormValidationService);

  public readonly OauthError = OauthError;
  public readonly LoginError = LoginError;
  public loading = true;
  public busy = false;
  public error: LoginError | null = null;
  public oauthError: OauthError | null = null;

  public readonly loginForm = new FormGroup({
      'email': this.controls.getEmailControl(),
      'password': new FormControl('', [Validators.required]),
    });
  public get email() { return this.loginForm.controls.email; }
  public get password() { return this.loginForm.controls.password; }

  public readonly emailErrors: Errors = {
    'required': 'An email must be provided',
    'pattern': 'Given email must be valid',
  };
  public readonly passwordErrors: Errors = { 'required': 'A password must be provided' };

  public showUsernameLogin = (): boolean => (new URLSearchParams(window.location.search).get('showUserLogin') ?? '') === 'true';

  public async ngOnInit(): Promise<void> {
    const { code, state, error } = this.activatedRoute.snapshot.queryParams;

    if (error) {
      if (state) {
        this.oauthError = OauthError.AuthorisationError;
      } else {
        this.error = error;
      }
    } else if (code || state) {
      await this.continueNomidioLogin(code, state);
    }

    this.loading = false;
  }

  public async login(): Promise<boolean> {
    await this.showDeprecationWarning();
    this.busy = true;
    this.loginService.enterEmailAddress(this.email.value);
    this.loginService.enterPassword(this.password.value);
    return this.router.navigate(['login', 'authcode']);
  }

  public async continueNomidioLogin(code: string, state: string): Promise<void> {
    this.busy = true;
    if (this.nomidioLoginService.isValidLogin(code, state)) {
      try {
        await this.loginService.loginWithCode(code);
        await this.router.navigate(['admin']);
      } catch (e) {
        console.error('Error in continueNomidioLogin', e);
        this.oauthError = OauthError.UnexpectedFailure;
      }
    } else {
      this.oauthError = OauthError.InvalidParameters;
    }
    this.busy = false;
  }

  public loginWithNomidio() {
    this.busy = true;
    try {
      this.nomidioLoginService.loginWithNomidio();
    } catch (e) {
        console.error('Error in loginWithNomidio', e);
      this.oauthError = OauthError.UnexpectedFailure;
    }
  }

  private showDeprecationWarning(): Promise<void> {
    const config = new MessageDialogConfig({
      theme: MessageDialogTheme.Alert,
      title: 'Deprecation Warning',
      message: [
        `Password login will be disabled in the future.`,
        `Please ensure you have registered a Nomidio ID for sign in.`
      ],
      primaryButton: { output: null, label: 'OK'}
    });
    config.disableClose = false;
    return firstValueFrom(this.dialog.open(MessageDialogComponent, config).afterClosed(), { defaultValue: undefined });
  }
}
